import { Injectable } from "@angular/core";
import { Script } from "@models/script";
import { BehaviorSubject } from "rxjs";

@Injectable({
	providedIn: "root"
})
export class ScriptManagerService {
	private injectedScriptsMap: Map<string, Script> = new Map<string, Script>();

	protected _eventsList: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

	public loadScript(script: Script): Promise<Script> {
		return new Promise((resolve) => {
			// Resolve if already loaded
			if (script.loaded) {
				return resolve(script);
			}

			// Resolve if the script was already injected before // don't add it again in DOM
			if (this.injectedScriptsMap.has(script.name)) {
				return resolve(script);
			}

			// Copy the Script properties to DOM's htmlScript
			script.fillHtmlModel();

			script.htmlScript.onload = () => {
				script.loaded = true;
				resolve(script);
			};
			script.htmlScript.onerror = () => {
				script.loaded = false;
				resolve(script);
			};
			if (!this.injectedScriptsMap.has(script.name)) {
				this.getDomScriptParent(script).appendChild(script.htmlScript);
				this.injectedScriptsMap.set(script.name, script);
			}
		});
	}

	public hasScript(name: string): boolean {
		return this.injectedScriptsMap.has(name);
	}

	/*
		Removes a script which was loaded before through ScriptManager.
		We keep stored all the loaded scripts inside injectedScriptsMap
	 */
	public removeScriptByName(scriptName: string): void {
		this.injectedScriptsMap.forEach((script: Script, name: string) => {
			if (name === scriptName) {
				// Remove it from DOM
				try {
					this.getDomScriptParent(script).removeChild(script.htmlScript);
				} catch (e) {
					console.error(e);
				}

				// Clear / remove it from list
				script.loaded = false;
				script.htmlScript = null;

				this.injectedScriptsMap.delete(name);
			}
		});
	}

	/*
		Removes any kind of script which contains a specific string.
		Eg: <script src="https://etrust-sandbox.electronicid.eu/v2/js/eid.core.js">
		And we want to search for all scripts which contain 'electronicid' in their src
	 */
	public removeScriptByStringPart(
		stringPart: string,
		location: string = "head",
		tagType: string = "script",
		searchProperty: string = "src"
	): void {
		const mainNode = document.getElementsByTagName(location)[0]; // Head || body
		const childNodes = mainNode.getElementsByTagName(tagType);

		Array.from(childNodes).forEach((node: HTMLElement) => {
			if (node[searchProperty] && node[searchProperty].includes(stringPart)) {
				document.getElementsByTagName(location)[0].removeChild(node);
			}
		});
	}

	/*
		CreateScript()
		* name - used only internally, for script manager list
		* content - the actual JavaScript code
		* locationType - 'tag' like <head><body> or other specific <tag>. Or 'id', to handle the element by id (eg: <div id='some-container'></div>)
		* location - the exact injection location (eg: 'head', 'body', 'my-custom-id-442') - where the script will be added
	 */
	public createScript(
		name: string,
		content: string,
		locationType: string = "tag",
		location: string = "head"
	): Script {
		const scriptElement: HTMLScriptElement = document.createElement("script");
		scriptElement.text = content;

		const newScript: Script = new Script(name);
		newScript.htmlScript = scriptElement;
		newScript.locationType = locationType;
		newScript.location = location;

		return newScript;
	}

	/*
		Get the parent placeholder, where the <script> will be added
 	*/
	private getDomScriptParent(script: Script): Element {
		// <head>, <body> or other specified <tag>
		if (script.locationType === "tag") {
			return document.getElementsByTagName(script.location)[0];
		}

		// <div id="my-container-id">
		if (script.locationType === "id") {
			return document.getElementById(script.location);
		}

		return null;
	}

	public addEvent(s: string): void {
		this._eventsList.next([...this.eventsList, s]);
	}

	public hasEvent(s: string): boolean {
		return this.eventsList.some((item) => item === s);
	}

	public removeEvent(s: string): void {
		this.eventsList = [...this.eventsList.filter((i) => i !== s)];
	}

	private get eventsList(): string[] {
		return this._eventsList.getValue();
	}

	private set eventsList(list: string[]) {
		this._eventsList.next(list);
	}
}
